/**
 * Generación de casos con solución única garantizada (MVP §3).
 *
 * 1. Cuadrado latino aleatorio de orden N.
 * 2. Sala y hora del crimen → el asesino queda determinado.
 * 3. Pool de todas las pistas verdaderas para esa solución.
 * 4. Mezclar y añadir pistas hasta que el solver devuelva exactamente 1 solución.
 * 5. Segunda pasada quitando pistas una a una mientras la unicidad se mantenga.
 */

import { buildCluePool, revealsCell } from './clues';
import { cellAt, randomLatinSquare } from './grid';
import { createRng } from './rng';
import { countSolutions, solveByPropagation } from './solver';
import type { Case, Clue, Difficulty, Grid, Theme } from './types';

export interface GenerateOptions {
  /** Orden del cuadrado: personajes = salas = horas = N. */
  n: number;
  /** Semilla explícita. Misma semilla y mismas opciones → mismo caso. */
  seed: string | number;
  /** Etiquetas del pack de contenido. Si falta, se usan marcadores neutros. */
  theme?: Theme;
  /** Tope de pistas `NOT_AT` en el pool (por defecto `2n`). */
  maxNotAt?: number;
}

/**
 * Tema de relleno. Existe para que el motor se pueda probar y usar solo; los nombres
 * y los enunciados de verdad vienen del pack de contenido.
 */
export const PLACEHOLDER_THEME: Theme = {
  id: 'placeholder',
  people: Array.from({ length: 8 }, (_, i) => `Persona ${i + 1}`),
  rooms: Array.from({ length: 8 }, (_, i) => `Sala ${i + 1}`),
  hours: Array.from({ length: 8 }, (_, i) => `Hora ${i + 1}`),
};

function labelsFor(theme: Theme, n: number): Pick<Case, 'people' | 'rooms' | 'hours'> {
  const short = (['people', 'rooms', 'hours'] as const).filter(
    (key) => theme[key].length < n,
  );
  if (short.length > 0) {
    throw new RangeError(
      `el tema "${theme.id}" no llega a N=${n} en: ${short.join(', ')}`,
    );
  }
  return {
    people: theme.people.slice(0, n),
    rooms: theme.rooms.slice(0, n),
    hours: theme.hours.slice(0, n),
  };
}

function classify(n: number, clueCount: number, needsGuessing: boolean): Difficulty {
  const byOrder = n <= 4 ? 'facil' : n === 5 ? 'media' : 'experta';
  return { label: needsGuessing ? 'experta' : byOrder, clueCount, needsGuessing };
}

/** Índice de la persona que ocupa `room` a la hora `hour`. */
export function murdererOf(solution: Grid, room: number, hour: number): number {
  for (let p = 0; p < solution.length; p += 1) {
    if (cellAt(solution, p, hour) === room) return p;
  }
  throw new Error(`sala ${room} vacía a la hora ${hour}: la rejilla no es latina`);
}

export function generateCase(options: GenerateOptions): Case {
  const { n, seed, theme = PLACEHOLDER_THEME } = options;
  if (!Number.isInteger(n) || n < 2) throw new RangeError(`N inválido: ${n}`);

  const rng = createRng(seed);
  const labels = labelsFor(theme, n);
  const solution = randomLatinSquare(n, rng);

  const crime = { room: rng.int(n), hour: rng.int(n) };
  const murderer = murdererOf(solution, crime.room, crime.hour);

  /*
   * Ninguna pista puede decir quién ocupa la celda del crimen: eso es el nombre del
   * asesino, y el pool de pistas verdaderas lo contiene (`AT` sobre esa hora y esa
   * sala, y `OPEN`/`FIRST_IN`/`CLOSE`/`LAST_IN` si el crimen cae a primera o última
   * hora). Peor aún: son las pistas más informativas del pool, así que la reducción a
   * conjunto mínimo las conserva de preferencia. Medido sobre 500 casos por cada N:
   * sin este filtro el 13–14 % se resolvía leyendo una sola línea del enunciado.
   *
   * El filtro va después de mezclar para no cambiar lo que consume el generador
   * aleatorio en `buildCluePool`: aquí solo se quitan elementos.
   */
  const pool = rng
    .shuffle(
      buildCluePool(
        solution,
        rng,
        options.maxNotAt === undefined ? {} : { maxNotAt: options.maxNotAt },
      ),
    )
    .filter((candidate) => !revealsCell(candidate, n, crime));

  const chosen: Clue[] = [];
  let unique = false;
  for (const candidate of pool) {
    chosen.push(candidate);
    if (countSolutions(chosen, n) === 1) {
      unique = true;
      break;
    }
  }
  if (!unique) {
    // El pool determina la rejilla salvo la celda del crimen, y esa queda forzada por
    // descarte: es la única persona que sobra a esa hora.
    throw new Error(`el pool no alcanzó unicidad para N=${n}, semilla ${String(seed)}`);
  }

  // Reducción a conjunto mínimo. Como quitar pistas nunca reduce el número de
  // soluciones, una sola pasada basta para que el resultado sea minimal: al terminar,
  // quitar cualquier pista que quede rompe la unicidad.
  const order = rng.shuffle([...chosen]);
  let minimal = chosen;
  for (const candidate of order) {
    const without = minimal.filter((c) => c.id !== candidate.id);
    if (countSolutions(without, n) === 1) minimal = without;
  }

  const needsGuessing = !solveByPropagation(minimal, n).solved;

  return {
    n,
    seed: String(seed),
    theme: theme.id,
    ...labels,
    crime,
    clues: minimal,
    solution,
    murderer,
    difficulty: classify(n, minimal.length, needsGuessing),
  };
}
